import { useEffect, useMemo, useRef, useState } from 'react';
import { LAYOUT_LIMITS, type ChatMailRef, type MailListItem } from '@jerome2/shared';
import { listDate } from '../lib/format.ts';
import { useStore } from '../lib/store.ts';
import { taskLine } from '../mail/MailList.tsx';
import { app, saveSettings } from '../state/app.ts';
import { chatStore } from '../state/chat.ts';
import { activeChatTab, columnCrowded, SIDE_MIN } from '../state/chatTabs.ts';
import { fitWidth, PANEL_MIN, pixelsText, useLayout, widthBounds } from '../state/layout.ts';
import { emptyList, FLAGGED_LIST, flaggedItems, listKey, loadList, mailStore } from '../state/mail.ts';
import { mailRefOf, mailsWithChat, markedMail, openMailTab } from '../state/mailTabs.ts';
import { tabs, type PaneId } from '../state/tabs.ts';
import { tasksStore } from '../state/tasks.ts';
import { errorToast } from '../state/toast.ts';
import { Icon } from '../ui/Icon.tsx';
import { Button, Spinner } from '../ui/primitives.tsx';
import { Resizer } from '../ui/Resizer.tsx';

// De kolom Gevlagd helemaal links (ticket 30961, opdracht van de eigenaar op 1 november 2026: "helemaal links nog een kolom met de
// lijst van al mijn gevlagde mails"): links van de gesprekkenkolom, over de volle hoogte onder de bovenbalk en op elk tabblad,
// in dezelfde stijl. Alle gevlagde mails, nieuwste bovenaan (`flaggedItems`: ook die met een lopende of klare taak, die het
// tabblad Mail bij Gevlagd verbergt), per pagina van 25 met Meer laden, zoals de maillijst; per mail afzender, onderwerp en datum,
// en een klein kenmerk als er een taak (de status van de nieuwste) of een gesprek bij hoort. Een klik opent het tabblad van die
// mail (state/mailTabs.ts: een tweede klik opent geen tweede tabblad); de mail van het actieve tabblad is gemarkeerd. Vlaggen zetten
// of weghalen gebeurt hier niet (dat kan in het mailpaneel van het tabblad of op Mail). De kolom klapt in tot een strook van 44 px
// met het aantal gevlagde mails volgens de server (`counts.flagged`, hetzelfde getal als Gevlagd in de filterbalk), en de stand is
// een instelling (`settings.flagged.visible`); de breedte stelt de eigenaar in met de greep op de rechterrand
// (`settings.layout.flaggedColumn`, 220 tot 480 px, de rest houdt 480 px, en op een chattabblad 824 px: SIDE_MIN, zodat het
// mailpaneel naast het gesprek blijft passen en een tabblad van een mail de mail niet als vel over alles heen legt; past de ingestelde
// breedte niet, dan is de kolom smaller en blijft de instelling staan). Op een tabblad zonder chat (Mail, Agenda) wijkt de
// lijst tot de strook als er met de lijst open minder dan 1240 px overblijft voor gesprekkenkolom en panelen, dezelfde regel als
// de gesprekkenkolom (`columnCrowded`, besluit b op 28083: Mail en Agenda houden hun breedte). Op een chattabblad wijkt ze als
// zelfs de smalste kolom (220 px) minder dan SIDE_MIN overlaat, dus onder 1044 px voor de kolom met de rest: met de smalle strook
// ernaast past het mailpaneel dan nog naast het gesprek. Uitklappen op de strook toont de lijst toch, zonder de instelling te
// veranderen, tot de reden vervalt (genoeg ruimte, of de wissel tussen een tabblad zonder chat en een chattabblad) of tot de
// eigenaar een mail kiest; dan wijkt ze weer, zodat de mail naast het gesprek kan staan. Op de telefoon is de lijst een vel vanuit
// de bovenbalk (FlaggedList `embedded`, in de schil).
// Vers houden zonder eigen timer (de begrenzer richting Graph, zie CLAUDE.md): bij het openen, als de lijsten verouderd zijn
// (`markStale`: nieuwe mail van de bewaking, herverbinden), als het aantal van de server verandert, bij terugkeer naar het venster
// na 15 s weg (zoals het tabblad Mail) en met de knop Vernieuwen. Met de standaardsortering deelt de kolom zijn lijst met Gevlagd
// op het tabblad Mail, en een verzoek dat al loopt, wordt niet nog eens gedaan.

const FLAGGED_LIST_ID = listKey(FLAGGED_LIST);
const EMPTY = emptyList();

/** De lijst van de kolom met de verversing hierboven; `count` is het aantal gevlagde mails volgens de server (null zolang onbekend). */
export function useFlaggedMails() {
  const list = useStore(mailStore, (s) => s.lists[FLAGGED_LIST_ID]) ?? EMPTY;
  const items = useMemo(() => flaggedItems(list.items), [list.items]);
  const count = useStore(tasksStore, (s) => (s.countsLoaded ? s.counts.flagged : null));
  useEffect(() => {
    void loadList(FLAGGED_LIST);
  }, []);
  useEffect(() => {
    if (list.stale && !list.loading) void loadList(FLAGGED_LIST, { force: true, background: true });
  }, [list.stale, list.loading]);
  // Het aantal van de server veranderde (een mail gevlagd of de vlag weg, ook in Outlook): de lijst opnieuw, op de achtergrond.
  const seen = useRef(count);
  useEffect(() => {
    if (seen.current !== null && count !== null && count !== seen.current) void loadList(FLAGGED_LIST, { force: true, background: true });
    seen.current = count;
  }, [count]);
  // Terug in het venster (na 15 s weg): wat intussen in Outlook gevlagd werd, staat er dan bij.
  useEffect(() => {
    let away = 0;
    const onBack = () => {
      if (document.visibilityState !== 'visible') {
        away = away || Date.now();
        return;
      }
      if (away && Date.now() - away > 15_000) void loadList(FLAGGED_LIST, { force: true, background: true });
      away = 0;
    };
    const onLeave = () => {
      away = away || Date.now();
    };
    document.addEventListener('visibilitychange', onBack);
    window.addEventListener('focus', onBack);
    window.addEventListener('blur', onLeave);
    return () => {
      document.removeEventListener('visibilitychange', onBack);
      window.removeEventListener('focus', onBack);
      window.removeEventListener('blur', onLeave);
    };
  }, []);
  return { list, items, count, more: () => void loadList(FLAGGED_LIST, { more: true }), refresh: () => void loadList(FLAGGED_LIST, { force: true }) };
}

/** De kolom op de desktop: de lijst met de greep op de rechterrand, of ingeklapt de strook. `pane`: het actieve paneel dat in beeld
 * is (daar opent een nieuw tabblad); `space`: de breedte van de kolom samen met wat rechts ervan staat tot de werkers (de rest houdt
 * 480 px, op een chattabblad SIDE_MIN). */
export function FlaggedColumn(props: { pane: PaneId; space: number }) {
  const visible = useStore(app, (s) => s.settings.flagged.visible);
  const marked = useStore(tabs, (s) => markedMail(s, props.pane));
  const column = useLayout('flaggedColumn');
  // Op een chattabblad (ook dat van een mail) houdt de rest SIDE_MIN in plaats van PANEL_MIN: daar moet het mailpaneel naast het
  // gesprek passen. widthBounds houdt PANEL_MIN over, dus de ruimte telt hier het verschil niet mee.
  const onChat = useStore(tabs, (s) => activeChatTab(s, props.pane) !== null);
  const space = onChat ? props.space - (SIDE_MIN - PANEL_MIN) : props.space;
  const bounds = widthBounds('flaggedColumn', space);
  const width = fitWidth('flaggedColumn', column.value, space);
  // Wijken op een tabblad zonder chat: de ruimte die met de lijst open overblijft voor gesprekkenkolom en panelen. Op een
  // chattabblad: als ook de smalste kolom het chatscherm onder SIDE_MIN zou duwen.
  const crowdedElsewhere = useStore(tabs, (s) => columnCrowded(s, props.pane, props.space - width));
  const crowded = onChat ? props.space - LAYOUT_LIMITS.flaggedColumn.min < SIDE_MIN : crowdedElsewhere;
  const [forced, setForced] = useState(false);
  // Uitklappen tegen de reden in geldt tot die reden verandert, ook bij de wissel tussen een chattabblad en een ander tabblad.
  useEffect(() => setForced(false), [crowded, onChat]);
  // Een mail gekozen: tegen de reden in uitgeklapt wijkt de lijst weer, zodat de mail naast het gesprek kan staan.
  const choose = (mail: ChatMailRef) => {
    setForced(false);
    void openMailTab(mail, props.pane);
  };
  const save = (on: boolean) => void saveSettings({ flagged: { visible: on } }).catch((e) => errorToast(e, 'Instelling niet bewaard'));
  const expand = () => {
    if (crowded) setForced(true);
    if (!visible) save(true);
  };
  const collapse = () => {
    setForced(false);
    save(false);
  };
  if (!visible || (crowded && !forced)) return <FlaggedStrip onExpand={expand} />;
  return (
    <>
      <FlaggedList width={width} markedId={marked} onOpen={choose} onCollapse={collapse} />
      <Resizer label="Breedte kolom Gevlagd" value={width} min={bounds.min} max={bounds.max} defaultValue={LAYOUT_LIMITS.flaggedColumn.default} side="left" valueText={pixelsText} onChange={column.change} onCommit={column.commit} />
    </>
  );
}

/** De lijst: in de kolom met een kop (het aantal, Vernieuwen en Inklappen) en een breedte; `embedded` vult een vel op de telefoon
 * (de titel staat dan op het vel). `markedId`: de mail van het actieve tabblad. */
export function FlaggedList(props: { onOpen: (mail: ChatMailRef) => void; markedId: string | null; width?: number | undefined; embedded?: boolean | undefined; onCollapse?: (() => void) | undefined }) {
  const { list, items, count, more, refresh } = useFlaggedMails();
  const chats = useStore(chatStore, (s) => s.chats);
  const withChat = useMemo(() => mailsWithChat(chats), [chats]);
  return (
    <aside className={`${props.embedded ? 'w-full' : 'shrink-0 border-r border-line'} h-full flex flex-col bg-paper`} style={props.embedded ? undefined : { width: props.width ?? LAYOUT_LIMITS.flaggedColumn.default }} aria-label="Gevlagd">
      <div className={`${props.embedded ? 'h-12' : 'h-11'} shrink-0 flex items-center gap-2 pl-3 pr-2 border-b border-line`}>
        <Icon name="flag" size={15} className="text-red shrink-0" />
        <span className={props.embedded ? 'sr-only' : 'font-medium'}>Gevlagd</span>
        {count !== null ? (
          <span data-count="flagged" className="text-[13px] text-muted tabular-nums" title={`${count} gevlagde mails volgens Outlook`}>
            {count}
          </span>
        ) : null}
        <button type="button" aria-label="Gevlagd vernieuwen" title="Gevlagd vernieuwen" onClick={refresh} className="ml-auto h-7 w-7 pointer-coarse:h-11 pointer-coarse:w-11 shrink-0 inline-flex items-center justify-center rounded-md text-ink-soft hover:bg-tint hover:text-ink">
          <Icon name="refresh" size={15} className={list.loading ? 'animate-spin' : ''} />
        </button>
        {props.onCollapse ? (
          <button type="button" aria-label="Gevlagd inklappen" title="Gevlagd inklappen" onClick={props.onCollapse} className="h-7 w-7 shrink-0 inline-flex items-center justify-center rounded-md text-ink-soft hover:bg-tint hover:text-ink">
            <Icon name="chevron" size={16} className="rotate-180" />
          </button>
        ) : null}
      </div>
      <FlaggedRows items={items} loading={list.loading} error={list.error} withChat={withChat} markedId={props.markedId} embedded={Boolean(props.embedded)} onOpen={props.onOpen} onRetry={refresh} />
      {list.more && items.length ? (
        <div className="shrink-0 p-2 border-t border-line text-center">
          <Button size="sm" variant="ghost" onClick={more} busy={list.loading}>
            Meer laden
          </Button>
        </div>
      ) : null}
    </aside>
  );
}

function FlaggedRows(props: { items: MailListItem[]; loading: boolean; error: string | null; withChat: Set<string>; markedId: string | null; embedded: boolean; onOpen: (mail: ChatMailRef) => void; onRetry: () => void }) {
  if (props.error && !props.items.length) {
    return (
      <div className="p-4 text-center text-[13px]">
        <p className="text-red mb-3">{props.error}</p>
        <Button size="sm" onClick={props.onRetry} icon="refresh">
          Opnieuw proberen
        </Button>
      </div>
    );
  }
  if (!props.items.length) return props.loading ? <Spinner className="p-6" /> : <p className="p-4 text-[13px] text-muted">Geen gevlagde mail.</p>;
  return (
    <ul className="flex-1 min-h-0 overflow-auto scroll-thin">
      {props.items.map((m) => {
        const marked = m.id === props.markedId;
        const task = taskLine(m);
        const sender = m.from?.name || m.from?.address || 'Onbekende afzender';
        return (
          <li key={m.id}>
            <button type="button" onClick={() => props.onOpen(mailRefOf(m))} aria-current={marked ? 'true' : undefined} className={`w-full text-left px-3 ${props.embedded ? 'py-3' : 'py-2.5'} border-b border-line ${marked ? 'bg-tint' : 'hover:bg-tint/50'}`}>
              <div className="flex items-center gap-2 text-[13px]">
                <span data-testid="gevlagd-afzender" className={`truncate flex-1 min-w-0 ${m.isRead ? 'text-ink-soft' : 'font-semibold text-ink'}`}>
                  {sender}
                </span>
                {task ? (
                  <span data-testid="gevlagd-taak" className={`shrink-0 inline-flex ${TONE_TEXT[task.tone]}`} title={`Taak: ${task.text}`}>
                    <Icon name="bolt" size={12} />
                    <span className="sr-only">Taak: {task.text}</span>
                  </span>
                ) : null}
                {props.withChat.has(m.id) ? (
                  <span data-testid="gevlagd-gesprek" className="shrink-0 inline-flex text-accent" title="Er is een gesprek over deze mail">
                    <Icon name="chat" size={12} />
                    <span className="sr-only">Gesprek</span>
                  </span>
                ) : null}
                <span className="shrink-0 text-[12px] text-muted tabular-nums">{listDate(m.received)}</span>
              </div>
              <div className={`text-[14px] leading-snug line-clamp-2 break-words ${m.isRead ? 'text-ink' : 'text-ink font-medium'}`}>{m.subject || '(geen onderwerp)'}</div>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

const TONE_TEXT: Record<'blue' | 'amber' | 'green' | 'red' | 'grey', string> = { blue: 'text-accent', amber: 'text-amber', green: 'text-green', red: 'text-red', grey: 'text-muted' };

/** De ingeklapte kolom: een strook van 44 px met de uitklapknop en het aantal gevlagde mails volgens de server. */
export function FlaggedStrip(props: { onExpand: () => void }) {
  const count = useStore(tasksStore, (s) => (s.countsLoaded ? s.counts.flagged : null));
  return (
    <aside className="w-11 shrink-0 h-full flex flex-col bg-paper border-r border-line" aria-label="Gevlagd">
      <button type="button" aria-label="Gevlagd uitklappen" title={count ? `Gevlagd uitklappen (${count} gevlagd)` : 'Gevlagd uitklappen'} onClick={props.onExpand} className="flex-1 flex flex-col items-center gap-2 pt-1.5 pb-3 text-ink-soft hover:bg-tint hover:text-ink">
        <span className="h-8 w-8 inline-flex items-center justify-center rounded-md">
          <Icon name="chevron" size={16} />
        </span>
        {count ? <span data-count="flagged" className="h-5 min-w-5 px-1 rounded-full bg-red text-white text-[12px] font-medium inline-flex items-center justify-center">{count}</span> : null}
        <span className="text-[12px] font-medium [writing-mode:vertical-rl]">Gevlagd</span>
      </button>
    </aside>
  );
}
